/**
 * @module services/monitoring
 * @description Servicio del rediseño del tablero de seguimiento (UC-REG-065).
 *              Wraps el endpoint /patients/:id/monitoring del API que devuelve
 *              adherencia + último score IA + activity timeline en una sola
 *              request.
 */
import api from "./api";

const monitoringService = {
  /**
   * @description Datos agregados de monitoreo de un paciente para la vista
   *              de paciente + fila del tablero. Una request, todo lo que el
   *              front necesita.
   *
   * @param {string} patientId
   * @param {Object} [params]
   * @param {string} [params.from] - ISO. Default backend: ahora - 30d.
   * @param {string} [params.to] - ISO. Default: ahora.
   * @returns {Promise<{
   *   adherence: { sevenDays, thirtyDays },
   *   lastConversation: Object|null,
   *   activity: Array,
   *   range: { from, to }
   * }>}
   */
  getPatientMonitoring(patientId, params = {}) {
    return api.get(`/patients/${patientId}/monitoring`, { params });
  },

  /**
   * @description Tablero org-level de la Central de Alertas. Una request:
   *              KPIs + alertas priorizadas + Foco IA de toda la organización.
   * @param {Object} [params]
   * @param {string} [params.doctorId] - Filtra por médico ("mi equipo")
   * @returns {Promise<{
   *   kpis: { criticas, alertasActivas, adherencia7d, sinContacto72h, enSeguimiento },
   *   alerts: Array, focoIA: Array, generatedAt: string
   * }>}
   */
  getCentral(params = {}) {
    return api.get("/monitoring/central", { params }).then((r) => r.data);
  },
};

/**
 * @description Keywords clínicas que disparan una alerta como "crítica" en el
 *              bloque cards del tablero. Se buscan dentro de
 *              `result.detectedSignals[].criterion` y `result.aiJustification`
 *              (lowercase) de la última healthConversation del paciente.
 *
 *              Editable a futuro desde admin (a definir UI).
 */
export const CRITICAL_KEYWORDS = [
  "ideación",
  "ideacion",
  "auto-lesión",
  "autolesion",
  "dolor torácico",
  "dolor toracico",
  "hemorragia",
  "fiebre alta",
  "falta de aire",
  "disnea severa",
  "pérdida de conciencia",
  "perdida de conciencia",
  "sangrado severo",
];

/**
 * @description Umbral de score IA que clasifica una conversación como crítica
 *              automáticamente (independiente de keywords).
 */
export const CRITICAL_SCORE_THRESHOLD = 9;

/**
 * @description Períodos de adherencia que mostramos en la UI.
 */
export const ADHERENCE_PERIODS = [
  { key: "sevenDays", label: "7d" },
  { key: "thirtyDays", label: "30d" },
];

/**
 * @description Orden de severidad de signals — high primero, low último.
 */
export const SIGNAL_SEVERITY_ORDER = ["high", "mid", "low"];

/**
 * @description Determina si una alerta es "crítica" según las reglas del MVP:
 *              - HealthConversation con score >= CRITICAL_SCORE_THRESHOLD, OR
 *              - Texto del signal o de la justificación contiene una keyword crítica
 *
 * @param {Object} alert - Item del array de alertas
 * @returns {boolean}
 */
export function isCriticalAlert(alert) {
  if (!alert) return false;
  // Caso 1: score IA alto
  if (alert.score != null && alert.score >= CRITICAL_SCORE_THRESHOLD)
    return true;
  // Caso 2: keyword crítica en signals o en justificación
  const haystack = [
    alert.aiJustification || "",
    ...(alert.signals || []).map(
      (s) => (s.criterion || "") + " " + (s.value || "")
    ),
    alert.text || "",
  ]
    .join(" ")
    .toLowerCase();
  return CRITICAL_KEYWORDS.some((kw) => haystack.includes(kw));
}

/**
 * @description Devuelve la flecha de tendencia (▲▼▶) según el delta.
 * @param {number|null} delta
 * @returns {{symbol: string, className: string}}
 */
export function trendIndicator(delta) {
  if (delta == null) return { symbol: "—", className: "trend--none" };
  if (delta > 2) return { symbol: "▲", className: "trend--up" };
  if (delta < -2) return { symbol: "▼", className: "trend--down" };
  return { symbol: "▶", className: "trend--flat" };
}

/**
 * @description Convierte un score 1-10 + delta en un string presentable
 *              ej. "8/10 ▲ +3"
 * @param {number} score
 * @param {number|null} delta
 * @returns {string}
 */
export function formatScoreWithDelta(score, delta) {
  if (score == null) return "—";
  const ind = trendIndicator(delta);
  const sign = delta > 0 ? `+${delta}` : delta;
  return delta == null ? `${score}/10` : `${score}/10 ${ind.symbol} ${sign}`;
}

export default monitoringService;
